import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useDroppable,
  useSensor,
  useSensors,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Edit,
  Email,
  Phone,
  Star,
  StarBorder,
  TrendingUp,
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MarkdownIt from 'markdown-it';
// Import MarkdownIt
import React, { useCallback, useEffect, useRef, useState } from 'react';
import MdEditor from 'react-markdown-editor-lite';
// Import MdEditor
import 'react-markdown-editor-lite/lib/index.css';
// Import MdEditor CSS
import {
  createList,
  createListItem,
  deleteList,
  deleteListItem,
  getAllBoardListItems,
  getAllBoardLists,
  getList,
  moveListItemToAnotherList,
  updateList,
  updateListItem,
  updatePositionListItem,
} from 'src/services/boards';
import ContactsService from 'src/services/contacts.services';
import { ConfirmDeleteCardDialog } from '../boardsAndLists/ConfirmDeleteCardDialog';
import { useContactDrawer } from './ContactDrawer';
import ContactsSearchAutocomplete from './ContactsSearchAutocomplete';
import ContactCardV2 from './ContactCardV2';

// INTERFACES
interface CardBoard {
  id: string;
  list_id: string;
  itemable_type: string | null;
  itemable_id: string | null;
  title: string | null;
  subtitle: string | null;
  description: string | null;
  order: number | null;
  tags: string[];
  status: string | null;
  owner: string | null;
  assigned_to: string | null;
  last_updated_by: any;
  created_by: any;
  created_at: string | null;
  updated_at: string | null;
}

interface BaseCard {
  id: string;
  order?: number;
  title: string;
}

interface ContactCardType extends BaseCard {
  type: 'contact';
  name?: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
  picture_url?: string;
  tags: string[];
  email?: string;
  phone?: string;
  notes?: string;
  deal_size?: number;
  source?: string;
  score?: number;
  last_contacted?: string;
}

interface BoardItemCardType extends BaseCard {
  type: 'board';
  subtitle: string | null;
  description: string | null;
  tags: string[];
  status: 'active' | 'inactive' | string | null;
  itemable_type?: string | null;
  itemable_id?: string | null;
  owner_object?: any; // Will contain the full Contact object when itemable_type is 'Contact'
}

type Card = ContactCardType | BoardItemCardType;

interface KanbanListType {
  id: string;
  title: string;
  subtitle?: string;
  cards: Card[];
  order?: number;
}

interface ActiveDraggedItem {
  id: string;
  type: 'card' | 'list';
  data: Card | KanbanListType;
}

// Props type for the dialog
export type IAddNewCardDialogProps = {
  onDialogOpen?: boolean;
  onCardClose?: () => void;
  cardTitle?: string;
  setCardTitle?: (title: string) => void;
  cardDescription?: string;
  setCardDescription?: (desc: string) => void;
  selectedContact?: any | null;
  setSelectedContact?: (contact: any | null) => void;
  onCardSubmit?: (data: {
    title: string;
    subtitle?: string;
    description: string;
    tags?: string[];
    itemable_type?: string | null;
    itemable_id?: string | null;
    clear_itemable?: boolean;
  }) => Promise<void>;
  editingCard?: Card | null; // Prop to pass the card being edited
};

const mdParser = new MarkdownIt();

export const IAddNewCardDialog = ({
  onDialogOpen: addCardOpen,
  onCardClose: handleAddCardClose,
  cardTitle,
  setCardTitle,
  cardDescription,
  setCardDescription,
  selectedContact,
  setSelectedContact,
  onCardSubmit,
  editingCard, // Receive the editing card
}: IAddNewCardDialogProps) => {
  const [subtitle, setSubtitle] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const darkMode = theme.palette.mode === 'dark';

  // Effect to populate form when dialog opens for editing
  useEffect(() => {
    if (addCardOpen) {
      if (editingCard && editingCard.type === 'board') {
        // Populate state from the card being edited
        setSubtitle(editingCard.subtitle || '');
        setTags(editingCard.tags || []);
        // If editing card has a contact itemable, set the selected contact
        if (editingCard.itemable_type === 'Contact' && editingCard.owner_object && setSelectedContact) {
          setSelectedContact(editingCard.owner_object);
        } else if (setSelectedContact) {
          setSelectedContact(null);
        }
      } else {
        // Reset state for a new card
        setSubtitle('');
        setTags([]);
        if (setSelectedContact) {
          setSelectedContact(null);
        }
      }
      setTagInput('');
    }
  }, [addCardOpen, editingCard, setSelectedContact]);

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag]);
    }
    setTagInput('');
  };

  const handleDeleteTag = (tagToDelete: string) => {
    setTags(tags.filter((tag) => tag !== tagToDelete));
  };

  const handleSubmit = async () => {
    if (!cardTitle || !cardTitle.trim()) return;
    setLoading(true);
    try {
      if (onCardSubmit) {
        const submitData: any = {
          title: cardTitle.trim(),
          subtitle: subtitle.trim(),
          description: cardDescription ? cardDescription.trim() : '',
          tags,
        };
        
        if (selectedContact) {
          // Adding or keeping a contact
          submitData.itemable_type = 'Contact';
          submitData.itemable_id = selectedContact.id;
        } else if (editingCard && editingCard.type === 'board' && editingCard.itemable_id && !selectedContact) {
          // Removing a contact - use clear_itemable flag
          submitData.clear_itemable = true;
        } else {
          // No contact selected and no previous contact
          submitData.itemable_type = null;
          submitData.itemable_id = null;
        }
        
        await onCardSubmit(submitData);
        // Only close if submit was successful
        if (handleAddCardClose) handleAddCardClose();
      }
    } catch (e) {
      console.error('Error submitting card:', e);
      // Don't close the dialog on error
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={addCardOpen || false}
      onClose={handleAddCardClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          background: darkMode
            ? alpha(theme.palette.background.paper, 0.95)
            : theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderRadius: fullScreen ? 0 : 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          backdropFilter: 'blur(10px)',
        },
      }}
    >
      <DialogTitle fontWeight="700" sx={{ color: theme.palette.text.primary }}>
        {editingCard ? 'Edit Card' : 'Add New Card'}
      </DialogTitle>
      <DialogContent>
        <TextField
          label="Title"
          value={cardTitle}
          onChange={(e) => setCardTitle && setCardTitle(e.target.value)}
          fullWidth
          margin="normal"
          required
          variant="outlined"
          sx={{
            '& .MuiInputBase-root': { color: theme.palette.text.primary },
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: alpha(theme.palette.divider, 0.3) },
              '&:hover fieldset': { borderColor: theme.palette.primary.main },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
              },
            },
            '& .MuiFormLabel-root': { color: theme.palette.text.secondary },
            '& .MuiFormLabel-root.Mui-focused': {
              color: theme.palette.primary.main,
            },
          }}
        />
        <TextField
          label="Subtitle"
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          fullWidth
          margin="normal"
          variant="outlined"
          sx={{
            '& .MuiInputBase-root': { color: theme.palette.text.primary },
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: alpha(theme.palette.divider, 0.3) },
              '&:hover fieldset': { borderColor: theme.palette.primary.main },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
              },
            },
            '& .MuiFormLabel-root': { color: theme.palette.text.secondary },
            '& .MuiFormLabel-root.Mui-focused': {
              color: theme.palette.primary.main,
            },
          }}
        />
        
        {/* Contact Selection */}
        <Box mt={2} mb={2}>
          <Typography
            variant="subtitle2"
            gutterBottom
            color={theme.palette.text.secondary}
          >
            Link to Contact (Optional)
          </Typography>
          {selectedContact ? (
            <Box
              sx={{
                position: 'relative',
                border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                borderRadius: 1,
                p: 1,
              }}
            >
              <IconButton
                size="small"
                onClick={() => setSelectedContact && setSelectedContact(null)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 1,
                  backgroundColor: alpha(theme.palette.background.paper, 0.9),
                  '&:hover': {
                    backgroundColor: theme.palette.background.paper,
                  },
                }}
              >
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </IconButton>
              <ContactCardV2
                contactId={selectedContact.id}
                expanded={false}
                selected={false}
                onSelect={() => {}}
              />
            </Box>
          ) : (
            <ContactsSearchAutocomplete
              onSelectContact={(contact) => setSelectedContact && setSelectedContact(contact)}
              preSelectedContactId={null}
              disableContactDialog={false}
            />
          )}
        </Box>
        
        <Box mt={2} mb={2}>
          <Typography
            variant="subtitle2"
            gutterBottom
            color={theme.palette.text.secondary}
          >
            Content of the card
          </Typography>
          {/* Aplica estilos dinâmicos para o MdEditor */}
          <style>
            {`
              .rc-md-editor {
                background-color: ${
                  darkMode
                    ? alpha(theme.palette.background.default, 0.5)
                    : theme.palette.background.paper
                };
                border-color: ${alpha(theme.palette.divider, 0.3)} !important;
              }
              .rc-md-editor .editor-container {
                background-color: ${
                  darkMode
                    ? alpha(theme.palette.background.default, 0.5)
                    : theme.palette.background.default
                };
              }
              .rc-md-editor .editor-container .sec-md .input {
                background-color: transparent;
                color: ${theme.palette.text.primary};
              }
              .rc-md-editor .tool-bar {
                background-color: ${
                  darkMode
                    ? alpha(theme.palette.background.paper, 0.7)
                    : alpha(theme.palette.grey[100], 0.7)
                };
                border-color: ${alpha(theme.palette.divider, 0.3)} !important;
              }
              .rc-md-editor .tool-bar .button {
                color: ${theme.palette.text.secondary};
              }
              .rc-md-editor .tool-bar .button:hover {
                color: ${theme.palette.text.primary};
                background-color: ${alpha(theme.palette.action.hover, 0.2)};
              }

              .rc-md-editor .rc-md-navigation{
                background: ${
                  darkMode
                    ? alpha(theme.palette.background.paper, 0.7)
                    : alpha(theme.palette.grey[100], 0.7)
                };
                border-bottom: ${
                  darkMode
                    ? `1px solid ${alpha(theme.palette.divider, 0.3)}`
                    : `1px solid ${alpha(theme.palette.divider, 0.3)}`
                };
              }

              .rc-md-editor .editor-container>.section{
                background: ${
                  darkMode
                    ? alpha(theme.palette.background.paper, 0.7)
                    : alpha(theme.palette.grey[100], 0.7)
                };
                border:none;
              }

              .rc-md-editor .rc-md-navigation.visible > div.navigation-nav.left > div > span.button:hover {
                color: ${darkMode ? '#fff' : '#000'} !important
              }
              
              .rc-md-editor .rc-md-navigation.visible > div.navigation-nav.left > div > span.button:first-child:hover {
                color: #000 !important
              }
            `}
          </style>
          <MdEditor
            value={cardDescription}
            style={{
              height: 200,
              borderRadius: '4px',
            }}
            view={{ menu: true, md: true, html: false }}
            renderHTML={(text) => mdParser.render(text)}
            onChange={({ text }) =>
              setCardDescription && setCardDescription(text)
            }
          />
        </Box>
        <Box mt={2}>
          <Typography
            variant="subtitle2"
            gutterBottom
            color={theme.palette.text.secondary}
          >
            Tags
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
            {tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                onDelete={() => handleDeleteTag(tag)}
                sx={{
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  '& .MuiChip-deleteIcon': {
                    color: theme.palette.text.secondary,
                  },
                }}
              />
            ))}
          </Box>
          <Box display="flex" gap={1}>
            <TextField
              label="Add tag"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddTag();
                }
              }}
              size="small"
              variant="outlined"
              sx={{
                '& .MuiInputBase-root': { color: theme.palette.text.primary },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: alpha(theme.palette.divider, 0.3),
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
                '& .MuiFormLabel-root': { color: theme.palette.text.secondary },
              }}
            />
            <Button
              onClick={handleAddTag}
              variant="outlined"
              disabled={!tagInput.trim()}
              sx={{
                color: theme.palette.text.secondary,
                borderColor: alpha(theme.palette.divider, 0.3),
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  background: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              Add
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px' }}>
        <Button
          onClick={handleAddCardClose}
          sx={{ color: theme.palette.text.secondary }}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!cardTitle || !cardTitle.trim() || loading}
          sx={{
            color: darkMode ? '#000' : '#fff',
            background: theme.palette.primary.main,
            '&:hover': { background: theme.palette.primary.dark },
            '&.Mui-disabled': {
              background: alpha(theme.palette.action.disabled, 0.1),
              color: theme.palette.action.disabled,
            },
          }}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
// --- MODAL COMPONENTS ---
const StageNameModal = ({
  open,
  onClose,
  onSave,
  title,
  initialValue = '',
  label = 'Stage Name',
  loading = false,
}) => {
  const [name, setName] = useState(initialValue);
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';

  useEffect(() => {
    if (open) {
      const descriptiveName = initialValue.replace(/^(\d+)-/, '');
      setName(descriptiveName);
    }
  }, [open, initialValue]);

  const handleSave = () => {
    if (name.trim() && !loading) {
      onSave(name.trim());
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          background: darkMode
            ? alpha(theme.palette.background.paper, 0.95)
            : theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          backdropFilter: 'blur(10px)',
        },
      }}
    >
      <DialogTitle fontWeight="700" sx={{ color: theme.palette.text.primary }}>
        {title}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          id="name"
          label={label}
          type="text"
          fullWidth
          variant="outlined"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSave()}
          sx={{
            mt: 2,
            '& .MuiInputBase-root': { color: theme.palette.text.primary },
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: alpha(theme.palette.divider, 0.3) },
              '&:hover fieldset': { borderColor: theme.palette.primary.main },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
              },
            },
            '& .MuiFormLabel-root': { color: theme.palette.text.secondary },
            '& .MuiFormLabel-root.Mui-focused': {
              color: theme.palette.primary.main,
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px' }}>
        <Button onClick={onClose} sx={{ color: theme.palette.text.secondary }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading}
          sx={{
            color: darkMode ? '#fff' : '#000',
            background: theme.palette.primary.main,
            '&:hover': { background: theme.palette.primary.dark },
            '&.Mui-disabled': {
              background: alpha(theme.palette.action.disabled, 0.1),
              color: theme.palette.action.disabled,
            },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ConfirmDeleteListDialog = ({
  open,
  onClose,
  onConfirm,
  listTitle,
  cardCount,
  loading,
  boardMode, // Added boardMode prop
}) => {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          background: darkMode
            ? alpha(theme.palette.background.paper, 0.95)
            : theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          backdropFilter: 'blur(10px)',
        },
      }}
    >
      <DialogTitle fontWeight="700" sx={{ color: theme.palette.text.primary }}>
        Delete "{listTitle}"?
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ color: theme.palette.text.secondary }}>
          Are you sure you want to delete this stage?
          {boardMode === 'contacts' &&
            cardCount > 0 &&
            ` The ${cardCount} card(s) in this list will be moved to "Backlog".`}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px' }}>
        <Button onClick={onClose} sx={{ color: theme.palette.text.secondary }}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          color="error"
          disabled={loading}
          sx={{
            color: '#fff',
            background: theme.palette.error.main,
            '&:hover': { background: theme.palette.error.dark },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- KANBAN COMPONENTS ---
type CardItemProps = {
  card: Card;
  isOverlay?: boolean;
  onToggleStatus?: (cardId: string) => void;
  onDelete?: (card: Card) => void;
  onToggleFavorite?: (cardId: string) => void;
  onEdit?: (card: Card) => void; // Prop to handle editing a card
  overlayWidth?: number;
};

const CardItem: React.FC<CardItemProps> = ({
  card,
  isOverlay,
  overlayWidth,
  onToggleStatus,
  onDelete,
  onToggleFavorite,
  onEdit, // Receive onEdit handler
}) => {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  const isFavorite = card.tags?.includes('isFavorite');
  const [isExpanded, setIsExpanded] = useState(false);
  const { openDrawer } = useContactDrawer();

  const displayName = React.useMemo(() => {
    if (card.type !== 'contact') return card.title;
    const baseName =
      card.name || `${card.first_name || ''} ${card.last_name || ''}`.trim();
    return baseName || card.title;
  }, [card]);

  const handleCardClick = (e: React.MouseEvent) => {
    // Prevent card from expanding when clicking on an icon button
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    if (card.type === 'contact') {
      setIsExpanded(!isExpanded);
    } else if (card.type === 'board' && onEdit) {
      onEdit(card); // Trigger the edit handler for board cards
    }
  };

  const handleAvatarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (card.type === 'contact') {
      openDrawer(card.id);
    }
  };

  // Enhanced contact card styling
  if (card.type === 'contact') {
    return (
      <>
        <Paper
          onClick={handleCardClick}
          sx={{
            p: isExpanded ? 2 : 1.5,
            pb: 2.5, // Extra bottom padding for expand indicator
            mb: isOverlay ? 0 : 1,
            borderRadius: 2,
            boxShadow: isOverlay ? theme.shadows[8] : theme.shadows[1],
            cursor: 'pointer',
            width: isOverlay ? overlayWidth || 282 : 'auto',
            position: 'relative',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            background: isExpanded
              ? `linear-gradient(135deg, ${alpha(
                  theme.palette.primary.main,
                  0.03
                )} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`
              : darkMode
              ? alpha(theme.palette.background.paper, 0.8)
              : theme.palette.background.paper,
            backdropFilter: 'blur(10px)',
            overflow: 'hidden',
            '&:hover': {
              boxShadow: theme.shadows[4],
              transform: 'translateY(-1px)',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              '& .card-actions': {
                opacity: 1,
              },
              '& .avatar-edit-overlay': {
                opacity: 1,
              },
            },
          }}
        >
          {/* Action buttons - hidden until hover */}
          <Box
            className="card-actions"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              display: 'flex',
              gap: 0.5,
              opacity: 0,
              transition: 'opacity 0.2s ease-in-out',
              zIndex: 2,
            }}
          >
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete && onDelete(card);
              }}
              sx={{
                width: 24,
                height: 24,
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                  color: theme.palette.error.main,
                },
              }}
            >
              <DeleteIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite && onToggleFavorite(card.id);
              }}
              sx={{
                width: 24,
                height: 24,
                color: isFavorite
                  ? theme.palette.warning.main
                  : theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.warning.main, 0.1),
                  color: theme.palette.warning.main,
                },
              }}
            >
              {isFavorite ? (
                <Star sx={{ fontSize: 14 }} />
              ) : (
                <StarBorder sx={{ fontSize: 14 }} />
              )}
            </IconButton>
          </Box>

          {/* Main content */}
          <Box
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pr: 4 }}
          >
            {/* Avatar with edit overlay */}
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              <Avatar
                src={card.picture_url}
                onClick={handleAvatarClick}
                sx={{
                  width: isExpanded ? 44 : 36,
                  height: isExpanded ? 44 : 36,
                  fontSize: isExpanded ? '1rem' : '0.9rem',
                  fontWeight: 600,
                  background: card.picture_url
                    ? 'transparent'
                    : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  color: '#fff',
                  border: `2px solid ${alpha(
                    theme.palette.primary.main,
                    isExpanded ? 0.3 : 0.2
                  )}`,
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: isExpanded ? theme.shadows[2] : 'none',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                }}
              >
                {!card.picture_url && displayName.charAt(0).toUpperCase()}
              </Avatar>

              {/* Edit icon overlay */}
              <Box
                className="avatar-edit-overlay"
                sx={{
                  position: 'absolute',
                  top: -2,
                  right: -2,
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  background: theme.palette.primary.main,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0,
                  transition: 'opacity 0.2s ease-in-out',
                  pointerEvents: 'none',
                  boxShadow: theme.shadows[2],
                }}
              >
                <Edit sx={{ fontSize: 10 }} />
              </Box>
            </Box>

            {/* Contact Info */}
            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              {/* Name and Company - more prominent display */}
              <Box sx={{ mb: 0.5 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    fontSize: isExpanded ? '1rem' : '0.95rem',
                    color: theme.palette.text.primary,
                    lineHeight: 1.1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    mb: card.company_name ? 0.25 : 0,
                  }}
                >
                  {displayName}
                </Typography>

                {card.company_name && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        background: theme.palette.primary.main,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        fontSize: isExpanded ? '0.85rem' : '0.8rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {card.company_name}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Contact info - show differently based on expanded state */}
              {!isExpanded ? (
                // Collapsed: Show compact info in a single line
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    mb: 0.5,
                  }}
                >
                  {card.phone && (
                    <Chip
                      size="small"
                      label="📞"
                      sx={{
                        height: 16,
                        fontSize: '0.65rem',
                        background: alpha(theme.palette.info.main, 0.1),
                        '& .MuiChip-label': { px: 0.5 },
                      }}
                    />
                  )}
                  {card.email && (
                    <Chip
                      size="small"
                      label="✉️"
                      sx={{
                        height: 16,
                        fontSize: '0.65rem',
                        background: alpha(theme.palette.info.main, 0.1),
                        '& .MuiChip-label': { px: 0.5 },
                      }}
                    />
                  )}
                  {card.source && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.disabled,
                        fontSize: '0.65rem',
                        fontStyle: 'italic',
                      }}
                    >
                      via {card.source}
                    </Typography>
                  )}
                </Box>
              ) : (
                // Expanded: Show full contact details
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    mb: 0.75,
                  }}
                >
                  {card.phone && (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                    >
                      <Phone
                        sx={{
                          fontSize: 14,
                          color: theme.palette.text.secondary,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.primary,
                          fontSize: '0.8rem',
                          fontWeight: 500,
                        }}
                      >
                        {card.phone}
                      </Typography>
                    </Box>
                  )}
                  {card.email && (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                    >
                      <Email
                        sx={{
                          fontSize: 14,
                          color: theme.palette.text.secondary,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.primary,
                          fontSize: '0.8rem',
                          fontWeight: 500,
                        }}
                      >
                        {card.email}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Score indicator with color scale */}
              {typeof card.score !== 'undefined' && card.score !== null && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    mb: 0.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TrendingUp
                      sx={{
                        fontSize: 14,
                        color:
                          card.score >= 80
                            ? '#4CAF50'
                            : card.score >= 50
                            ? '#81C784'
                            : '#64B5F6',
                      }}
                    />
                    <Box
                      sx={{
                        width: 40,
                        height: 8,
                        borderRadius: 4,
                        background: alpha(theme.palette.divider, 0.1),
                        position: 'relative',
                        overflow: 'hidden',
                        border: `1px solid ${alpha(
                          theme.palette.divider,
                          0.2
                        )}`,
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          height: '100%',
                          width: `${Math.min(card.score, 100)}%`,
                          background: `linear-gradient(to right,
                            #64B5F6 0%,
                            #42A5F5 20%,
                            #66BB6A 40%,
                            #81C784 60%,
                            #66BB6A 80%,
                            #4CAF50 100%)`,
                          borderRadius: 4,
                          transition: 'width 0.3s ease-in-out',
                          boxShadow: `0 0 8px ${alpha(
                            card.score >= 80
                              ? '#4CAF50'
                              : card.score >= 50
                              ? '#81C784'
                              : '#64B5F6',
                            0.6
                          )}`,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        color:
                          card.score >= 80
                            ? '#4CAF50'
                            : card.score >= 50
                            ? '#66BB6A'
                            : '#64B5F6',
                        textShadow: `0 0 4px ${alpha(
                          card.score >= 80
                            ? '#4CAF50'
                            : card.score >= 50
                            ? '#81C784'
                            : '#64B5F6',
                          0.3
                        )}`,
                      }}
                    >
                      {card.score}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Tags and metadata in horizontal scrollable row */}
              {!isExpanded && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    pb: 0.5,
                    mb: 0.5,
                    '&::-webkit-scrollbar': {
                      height: 3,
                    },
                    '&::-webkit-scrollbar-track': {
                      background: alpha(theme.palette.divider, 0.1),
                      borderRadius: 1.5,
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: alpha(theme.palette.primary.main, 0.2),
                      borderRadius: 1.5,
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.3),
                      },
                    },
                  }}
                >
                  {/* Show first line of notes as a preview */}
                  {card.notes && (
                    <Chip
                      size="small"
                      label={
                        card.notes.split('\n')[0].substring(0, 30) +
                        (card.notes.length > 30 ? '...' : '')
                      }
                      sx={{
                        height: 16,
                        fontSize: '0.6rem',
                        background: alpha(theme.palette.warning.main, 0.08),
                        color: theme.palette.text.secondary,
                        border: `1px solid ${alpha(
                          theme.palette.warning.main,
                          0.2
                        )}`,
                        flexShrink: 0,
                        '& .MuiChip-label': { px: 0.5 },
                      }}
                    />
                  )}

                  {/* Show relevant tags */}
                  {card.tags &&
                    card.tags
                      .filter((t) => t !== 'isFavorite')
                      .map((tag) => (
                        <Chip
                          key={tag}
                          size="small"
                          label={tag}
                          sx={{
                            height: 16,
                            fontSize: '0.6rem',
                            background: alpha(
                              theme.palette.secondary.main,
                              0.08
                            ),
                            color: theme.palette.secondary.main,
                            border: `1px solid ${alpha(
                              theme.palette.secondary.main,
                              0.2
                            )}`,
                            flexShrink: 0,
                            '& .MuiChip-label': { px: 0.5 },
                          }}
                        />
                      ))}

                  {/* Deal size indicator */}
                  {card.deal_size > 0 && (
                    <Chip
                      size="small"
                      label={`$${
                        card.deal_size >= 1000
                          ? `${(card.deal_size / 1000).toFixed(0)}k`
                          : card.deal_size
                      }`}
                      sx={{
                        height: 16,
                        fontSize: '0.6rem',
                        background: alpha(theme.palette.success.main, 0.1),
                        color: theme.palette.success.main,
                        fontWeight: 700,
                        border: `1px solid ${alpha(
                          theme.palette.success.main,
                          0.3
                        )}`,
                        flexShrink: 0,
                        '& .MuiChip-label': { px: 0.5 },
                      }}
                    />
                  )}

                  {/* Last contacted */}
                  {card.last_contacted && (
                    <Chip
                      size="small"
                      label={`Last: ${new Date(
                        card.last_contacted
                      ).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}`}
                      sx={{
                        height: 16,
                        fontSize: '0.6rem',
                        background: alpha(theme.palette.info.main, 0.08),
                        color: theme.palette.info.main,
                        border: `1px solid ${alpha(
                          theme.palette.info.main,
                          0.2
                        )}`,
                        flexShrink: 0,
                        '& .MuiChip-label': { px: 0.5 },
                      }}
                    />
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {/* Expand indicator - minimal arrow at bottom */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 2,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 20,
              height: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: alpha(theme.palette.text.secondary, 0.4),
              transition: 'all 0.2s ease-in-out',
              cursor: 'pointer',
              '&:hover': {
                color: theme.palette.primary.main,
              },
            }}
          >
            <Box
              sx={{
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease-in-out',
                display: 'flex',
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z" />
              </svg>
            </Box>
          </Box>

          {/* Notes Preview - only show if expanded */}
          {card.notes && isExpanded && (
            <Box
              sx={{
                mt: 1.5,
                pt: 1.5,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                animation: 'fadeIn 0.3s ease-in-out',
                '@keyframes fadeIn': {
                  from: { opacity: 0, transform: 'translateY(-10px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.disabled,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  mb: 0.5,
                }}
              >
                Notes
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.8rem',
                  lineHeight: 1.4,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  background: alpha(theme.palette.action.hover, 0.03),
                  borderRadius: 1,
                  p: 1,
                  border: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
                }}
              >
                {card.notes}
              </Typography>
            </Box>
          )}

          {/* Expandable Details */}
          {isExpanded &&
            (card.deal_size > 0 || card.source || card.tags?.length > 0) && (
              <Box
                sx={{
                  mt: 1.5,
                  pt: 1.5,
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  animation: 'slideIn 0.3s ease-in-out',
                  '@keyframes slideIn': {
                    from: { opacity: 0, transform: 'translateY(-10px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                }}
              >
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  {card.deal_size > 0 && (
                    <Paper
                      elevation={0}
                      sx={{
                        px: 1.5,
                        py: 0.75,
                        background: `linear-gradient(135deg, ${alpha(
                          theme.palette.success.main,
                          0.1
                        )} 0%, ${alpha(
                          theme.palette.success.light,
                          0.05
                        )} 100%)`,
                        border: `1px solid ${alpha(
                          theme.palette.success.main,
                          0.2
                        )}`,
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '0.65rem',
                        }}
                      >
                        Deal Size
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.success.main,
                          fontSize: '0.85rem',
                        }}
                      >
                        ${card.deal_size.toLocaleString()}
                      </Typography>
                    </Paper>
                  )}

                  {card.source && (
                    <Paper
                      elevation={0}
                      sx={{
                        px: 1.5,
                        py: 0.75,
                        background: alpha(theme.palette.info.main, 0.08),
                        border: `1px solid ${alpha(
                          theme.palette.info.main,
                          0.2
                        )}`,
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '0.65rem',
                        }}
                      >
                        Source
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.info.main,
                          fontSize: '0.8rem',
                        }}
                      >
                        {card.source}
                      </Typography>
                    </Paper>
                  )}
                </Box>

                {card.tags &&
                  card.tags.length > 0 &&
                  card.tags.filter((t) => t !== 'isFavorite').length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {card.tags
                        .filter((t) => t !== 'isFavorite')
                        .map((tag) => (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              background: alpha(
                                theme.palette.primary.main,
                                0.08
                              ),
                              color: theme.palette.primary.main,
                              border: `1px solid ${alpha(
                                theme.palette.primary.main,
                                0.2
                              )}`,
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                        ))}
                    </Box>
                  )}
              </Box>
            )}
        </Paper>

        {/* Contact details handled by global ContactDrawer */}
      </>
    );
  }

  // Board card styling - consistent for all board cards
  if (card.type === 'board') {
    return (
      <>
        <Paper
          sx={{
            mb: isOverlay ? 0 : 1,
            borderRadius: 2,
            boxShadow: isOverlay ? theme.shadows[8] : theme.shadows[1],
            cursor: isOverlay ? 'grabbing' : 'pointer',
            width: isOverlay ? overlayWidth || 282 : 'auto',
            wordBreak: 'break-word',
            position: 'relative',
            transition: 'all 0.2s ease-in-out',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            background: darkMode
              ? alpha(theme.palette.background.paper, 0.8)
              : theme.palette.background.paper,
            backdropFilter: 'blur(10px)',
            overflow: 'hidden',
            '&:hover': {
              boxShadow: theme.shadows[4],
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              transform: 'translateY(-1px)',
            },
            '&:hover .delete-icon-button': {
              opacity: 1,
              pointerEvents: 'auto',
            },
          }}
        >
          {/* Main content area */}
          <Box
            onClick={handleCardClick}
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'flex-start',
              position: 'relative',
              color: theme.palette.text.primary,
            }}
          >
            <Box
              sx={{
                top: 4,
                right: 4,
                color: theme.palette.text.secondary,
                position: 'absolute',
                display: 'flex',
                gap: 0.5,
                zIndex: 2,
              }}
            >
              <IconButton
                className="delete-icon-button"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete && onDelete(card);
                }}
                sx={{
                  opacity: 0,
                  pointerEvents: 'none',
                  transition: 'opacity 0.2s',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                    color: theme.palette.error.main,
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleFavorite && onToggleFavorite(card.id);
                }}
                sx={{
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.warning.main, 0.1),
                  },
                }}
              >
                {isFavorite ? (
                  <Star
                    fontSize="small"
                    sx={{ color: theme.palette.warning.main }}
                  />
                ) : (
                  <StarBorder
                    fontSize="small"
                    sx={{ color: theme.palette.text.secondary }}
                  />
                )}
              </IconButton>
            </Box>

            {card.type === 'board' && (
              <Box
                sx={{
                  mr: 1,
                  mt: 0.5,
                  color: theme.palette.primary.main,
                  cursor: onToggleStatus ? 'pointer' : 'default',
                  display: 'flex',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleStatus && onToggleStatus(card.id);
                }}
              >
                {card.status === 'active' ? (
                  <RadioButtonUncheckedIcon />
                ) : (
                  <CheckCircleIcon />
                )}
              </Box>
            )}

            <Box sx={{ flexGrow: 1, pr: 4 }}>
              <Typography
                variant="body1"
                fontWeight={500}
                sx={{ color: theme.palette.text.primary }}
              >
                {displayName}
              </Typography>

              {card.type === 'board' && (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {card.subtitle && (
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {card.subtitle}
                    </Typography>
                  )}
                  {card.description && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {card.description}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {/* Footer section for itemables */}
          {card.itemable_type && card.owner_object && (
            <Box
              sx={{
                backgroundColor: darkMode
                  ? alpha(theme.palette.background.default, 0.3)
                  : alpha(theme.palette.grey[50], 0.5),
                p: 1,
              }}
            >
              {card.itemable_type === 'Contact' && (
                <ContactCardV2
                  contactId={card.itemable_id!}
                  expanded={false}
                  selected={false}
                  onSelect={() => {}}
                />
              )}
              {/* Future itemable types can be added here */}
              {/* {card.itemable_type === 'Note' && <NoteCard ... />} */}
              {/* {card.itemable_type === 'Message' && <MessageCard ... />} */}
              {/* {card.itemable_type === 'Conversation' && <ConversationCard ... />} */}
              {/* {card.itemable_type === 'ListItem' && <ListItemCard ... />} */}
              {/* {card.itemable_type === 'Channel' && <ChannelCard ... />} */}
            </Box>
          )}
        </Paper>
      </>
    );
  }

  // Default return for any other card types
  return null;
};

type ListColumnItemProps = {
  title: string;
  cards: Card[];
  isOverlay?: boolean;
};

const ListColumnItem: React.FC<ListColumnItemProps> = ({
  title,
  cards,
  isOverlay,
}) => {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';

  return (
    <Paper
      sx={{
        minWidth: 300,
        maxWidth: 300,
        background: darkMode
          ? alpha(theme.palette.background.default, 0.95)
          : theme.palette.background.default,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        overflow: 'hidden',
        height: 'fit-content',
        maxHeight: isOverlay ? 'calc(100vh - 128px)' : 'auto',
        opacity: isOverlay ? 0.95 : 1,
        boxShadow: isOverlay ? theme.shadows[8] : 'none',
        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        backdropFilter: 'blur(10px)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1.5,
          background: darkMode
            ? alpha(theme.palette.background.paper, 0.8)
            : theme.palette.background.paper,
          borderTopLeftRadius: 2,
          borderTopRightRadius: 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <DragIndicatorIcon
          fontSize="small"
          sx={{ color: theme.palette.text.secondary, mr: 1 }}
        />
        <Typography
          variant="subtitle1"
          sx={{ color: theme.palette.text.primary }}
          fontWeight={700}
        >
          {title}
        </Typography>
      </Box>
      <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.1) }} />
      <Box
        sx={{
          p: 1,
          flexGrow: 1,
          overflowY: 'auto',
          maxHeight: isOverlay ? '200px' : 'auto',
        }}
      >
        {isOverlay &&
          cards
            .slice(0, 3)
            .map((card) => (
              <CardItem key={`overlay-card-${card.id}`} card={card} isOverlay />
            ))}
        {!isOverlay &&
          cards.map((card) => <SortableCard key={card.id} card={card} />)}{' '}
        {isOverlay && cards.length > 3 && (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              p: 1,
              textAlign: 'center',
              display: 'block',
            }}
          >
            ... and more {cards.length - 3}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

type SortableCardProps = {
  card: Card;
  onToggleStatus?: (cardId: string) => void;
  onDelete?: (card: Card) => void;
  onToggleFavorite?: (cardId: string) => void;
  onEdit?: (card: Card) => void; // Prop to handle editing a card
};

const SortableCard: React.FC<SortableCardProps> = ({
  card,
  onToggleStatus,
  onDelete,
  onToggleFavorite,
  onEdit, // Receive onEdit handler
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: card.id,
    data: {
      type: 'card',
      item: card,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{ visibility: isDragging ? 'hidden' : 'visible' }}
      {...attributes}
      {...listeners}
    >
      <CardItem
        card={card}
        onToggleStatus={onToggleStatus}
        onDelete={onDelete}
        onToggleFavorite={onToggleFavorite}
        onEdit={onEdit} // Pass onEdit handler to CardItem
      />
    </Box>
  );
};

type KanbanListColumnProps = {
  list: KanbanListType;
  onAddCard: (listId: string) => void;
  onToggleCardStatus: (cardId: string) => void;
  onDeleteCard: (card: Card) => void;
  onRenameContactList: (list: KanbanListType) => void;
  onDeleteContactList: (list: KanbanListType) => void;
  onRenameBoardList: (list: KanbanListType) => void;
  onDeleteBoardList: (list: KanbanListType) => void;
  onToggleFavorite: (cardId: string) => void;
  onEditCard: (card: Card) => void; // Prop to handle editing a card
  boardMode: 'contacts' | 'boards';
};

const KanbanListColumn: React.FC<KanbanListColumnProps> = ({
  list,
  onAddCard,
  onToggleCardStatus,
  onDeleteCard,
  onRenameContactList,
  onDeleteContactList,
  onRenameBoardList,
  onDeleteBoardList,
  onToggleFavorite,
  onEditCard, // Receive onEditCard handler
  boardMode,
}) => {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const {
    attributes,
    listeners,
    setNodeRef: setSortableNodeRef,
    transform,
    transition,
    isDragging: isListDragging,
  } = useSortable({
    id: list.id,
    data: {
      type: 'list',
      item: list,
    },
  });

  const { setNodeRef: setDroppableNodeRef, isOver: isOverColumnForCardDrop } =
    useDroppable({
      id: list.id,
      data: {
        type: 'list',
        accepts: ['card'],
      },
    });

  const combinedRef = (node: HTMLElement | null) => {
    setSortableNodeRef(node);
    setDroppableNodeRef(node);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isListDragging ? 0.3 : 1,
    cursor: isListDragging ? 'grabbing' : 'grab',
  };

  const listSx = {
    minWidth: 300,
    maxWidth: 300,
    background: darkMode
      ? alpha(theme.palette.background.default, 0.95)
      : theme.palette.background.default,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 2,
    overflow: 'hidden',
    height: 'fit-content',
    maxHeight: 'calc(100vh - 64px)',
    border:
      isOverColumnForCardDrop && !isListDragging
        ? `2px dashed ${theme.palette.primary.main}`
        : `2px solid transparent`,
    boxShadow:
      isOverColumnForCardDrop && !isListDragging
        ? `0 0 10px ${alpha(theme.palette.primary.main, 0.5)}`
        : 'none',
  };

  return (
    <Paper
      ref={combinedRef}
      style={style}
      sx={listSx}
      {...attributes}
      className="kanban-list-paper"
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          minHeight: 56,  // Fixed minimum height to prevent jumping
          background: darkMode
            ? alpha(theme.palette.background.paper, 0.8)
            : theme.palette.background.paper,
          cursor: 'grab',
          borderTopLeftRadius: 2,
          borderTopRightRadius: 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
        {...listeners}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <DragIndicatorIcon
            fontSize="small"
            sx={{ color: theme.palette.text.secondary, mr: 1 }}
          />
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ color: theme.palette.text.primary }}
              fontWeight={700}
              noWrap
            >
              {boardMode === 'contacts' && list.id !== 'Backlog'
                ? list.title.replace(/^(\d+)-/, '')
                : list.title}
            </Typography>
            {list.subtitle && (
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary }}
                noWrap
              >
                {list.subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Three dot menu - always rendered but visibility controlled */}
        {((boardMode === 'contacts' && list.id !== 'Backlog') || boardMode === 'boards') && (
          <IconButton
            size="small"
            onClick={handleMenuClick}
            sx={{
              color: theme.palette.text.secondary,
              opacity: isHovered || open ? 1 : 0,
              pointerEvents: isHovered || open ? 'auto' : 'none',
              transition: 'opacity 0.2s ease',
              '&:hover': {
                backgroundColor: alpha(theme.palette.action.hover, 0.2),
              },
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}

        {/* Dropdown Menu */}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              mt: 0.5,
              minWidth: 120,
            },
          }}
        >
          {boardMode === 'contacts' ? (
            [
              <MenuItem
                key="rename"
                onClick={() => {
                  handleMenuClose();
                  onRenameContactList(list);
                }}
                sx={{ fontSize: '0.875rem' }}
              >
                <Edit fontSize="small" sx={{ mr: 1 }} />
                Rename
              </MenuItem>,
              <MenuItem
                key="delete"
                onClick={() => {
                  handleMenuClose();
                  onDeleteContactList(list);
                }}
                sx={{ fontSize: '0.875rem', color: theme.palette.error.main }}
              >
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                Delete
              </MenuItem>
            ]
          ) : (
            [
              <MenuItem
                key="rename"
                onClick={() => {
                  handleMenuClose();
                  onRenameBoardList(list);
                }}
                sx={{ fontSize: '0.875rem' }}
              >
                <Edit fontSize="small" sx={{ mr: 1 }} />
                Rename
              </MenuItem>,
              <MenuItem
                key="delete"
                onClick={() => {
                  handleMenuClose();
                  onDeleteBoardList(list);
                }}
                sx={{ fontSize: '0.875rem', color: theme.palette.error.main }}
              >
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                Delete
              </MenuItem>
            ]
          )}
        </Menu>
      </Box>
      <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.1) }} />
      <Box
        sx={{
          flexGrow: 1,
          p: 1,
          overflowY: 'auto',
          minHeight: 80,
          maxHeight: 'calc(100vh - 200px)', // Ensure lists don't exceed viewport
          paddingBottom: 2, // Add bottom padding within lists
        }}
      >
        {list.cards.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              minHeight: 60,
              p: 2,
              textAlign: 'center',
              color: theme.palette.text.disabled,
              border: `2px dashed ${alpha(theme.palette.divider, 0.3)}`,
              borderRadius: 1,
              background: alpha(theme.palette.action.hover, 0.02),
            }}
          >
            <Typography variant="body2">
              Column without {boardMode === 'contacts' ? 'contact' : 'card'}.
            </Typography>
            <Typography variant="caption">
              Please drop a {boardMode === 'contacts' ? 'contact' : 'card'}{' '}
              here.
            </Typography>
          </Box>
        ) : (
          <SortableContext
            items={list.cards.map((c) => c.id)}
            strategy={verticalListSortingStrategy}
          >
            {list.cards.map((card) => (
              <SortableCard
                key={card.id}
                card={card}
                onToggleStatus={onToggleCardStatus}
                onDelete={onDeleteCard}
                onToggleFavorite={onToggleFavorite}
                onEdit={onEditCard} // Pass onEditCard handler to SortableCard
              />
            ))}
          </SortableContext>
        )}
      </Box>
      {boardMode === 'boards' && (
        <Button
          startIcon={<AddIcon />}
          sx={{
            m: 1,
            px: 2,
            py: 1.7,
            color: theme.palette.text.primary,
            background: alpha(theme.palette.action.hover, 0.1),
            borderRadius: 1.8,
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            '&:hover': {
              background: alpha(theme.palette.primary.main, 0.1),
              borderColor: alpha(theme.palette.primary.main, 0.3),
              color: theme.palette.primary.main,
            },
            textTransform: 'none',
            fontSize: '0.9rem',
            fontWeight: 500,
            justifyContent: 'flex-start',
            width: 'auto',
            transition: 'all 0.2s ease-in-out',
          }}
          onClick={() => onAddCard(list.id)}
        >
          Add a card
        </Button>
      )}
    </Paper>
  );
};

type AddNewColumnButtonProps = {
  onClick: () => void;
  typeOfRender?: 'contacts' | 'boards';
};

const AddNewColumnButton = ({
  onClick,
  typeOfRender,
}: AddNewColumnButtonProps) => {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';

  return (
    <Tooltip title="Add new column" placement="top">
      <Paper
        onClick={onClick}
        sx={{
          minWidth: 325,
          maxWidth: 325,
          height: 'fit-content',
          maxHeight: 'calc(100vh - 64px)',
          background: darkMode
            ? alpha(theme.palette.background.default, 0.95)
            : theme.palette.background.default,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 2,
          cursor: 'pointer',
          border: `2px dashed ${alpha(theme.palette.divider, 0.3)}`,
          color: theme.palette.text.secondary,
          p: 2,
          transition: 'all 0.2s ease-in-out',
          backdropFilter: 'blur(10px)',
          '&:hover': {
            background: alpha(theme.palette.primary.main, 0.05),
            borderColor: alpha(theme.palette.primary.main, 0.3),
            color: theme.palette.primary.main,
            transform: 'translateY(-2px)',
            boxShadow: theme.shadows[4],
          },
        }}
      >
        <PlaylistAddIcon sx={{ fontSize: 40, mb: 1 }} />
        <Typography variant="subtitle1" fontWeight={700}>
          New {typeOfRender === 'contacts' ? 'Contact Stage' : 'Column'}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          (Click to add new column)
        </Typography>
      </Paper>
    </Tooltip>
  );
};

type CustomScrollbarProps = {
  containerRef: React.RefObject<HTMLDivElement>;
};

const CustomScrollbar: React.FC<CustomScrollbarProps> = ({ containerRef }) => {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [clientWidth, setClientWidth] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartScrollLeft, setDragStartScrollLeft] = useState(0);
  const thumbRef = useRef<HTMLDivElement>(null);

  const updateScrollbarMetrics = useCallback(() => {
    if (!containerRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
    setScrollLeft(scrollLeft);
    setScrollWidth(scrollWidth);
    setClientWidth(clientWidth);
  }, [containerRef]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    updateScrollbarMetrics();
    const handleScroll = () => updateScrollbarMetrics();
    const handleResize = () => updateScrollbarMetrics();
    const resizeObserver = new ResizeObserver(updateScrollbarMetrics);

    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
    };
  }, [containerRef, updateScrollbarMetrics]);

  const thumbWidth = Math.max((clientWidth / scrollWidth) * 100, 10);
  const thumbLeft =
    scrollWidth > clientWidth
      ? (scrollLeft / (scrollWidth - clientWidth)) * (100 - thumbWidth)
      : 0;

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragStartScrollLeft(containerRef.current?.scrollLeft || 0);
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;
      const deltaX = e.clientX - dragStartX;
      const newScrollLeft = dragStartScrollLeft + deltaX;
      containerRef.current.scrollLeft = newScrollLeft;
    },
    [isDragging, dragStartX, dragStartScrollLeft, containerRef]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  if (scrollWidth <= clientWidth) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: '20px',
        backgroundColor: darkMode
          ? theme.palette.grey[900]
          : theme.palette.grey[100],
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        px: 2,
        backdropFilter: 'blur(10px)',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '12px',
          backgroundColor: darkMode
            ? theme.palette.grey[800]
            : theme.palette.grey[200],
          borderRadius: '3px',
        }}
      >
        <Box
          ref={thumbRef}
          sx={{
            position: 'absolute',
            height: '100%',
            backgroundColor: isDragging
              ? theme.palette.primary.main
              : alpha(theme.palette.primary.main, 0.8),
            borderRadius: '3px',
            cursor: 'grab',
            transition: isDragging ? 'none' : 'background-color 0.2s',
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
            },
          }}
          style={{
            width: `${thumbWidth}%`,
            left: `${thumbLeft}%`,
          }}
          onMouseDown={handleMouseDown}
        />
      </Box>
    </Box>
  );
};

// --- DATA ADAPTER ---
const adaptApiResponseToKanbanLists = (
  rawLists: any[],
  rawCards: { [listId: string]: any[] },
  mode: 'contacts' | 'boards'
): KanbanListType[] => {
  return rawLists.map((list) => {
    const cardsForList = rawCards[list.id] || [];
    const adaptedCards: Card[] = cardsForList.map((item: any): Card => {
      if (mode === 'contacts') {
        return {
          type: 'contact',
          id: item.id,
          title:
            item.name ||
            `${item.first_name || ''} ${item.last_name || ''}`.trim() ||
            item.phone_number ||
            item.email ||
            '', // Fallback without forbidden labels
          name: item.name,
          first_name: item.first_name,
          last_name: item.last_name,
          company_name: item.company_name,
          picture_url: item.picture_url,
          order: item.position,
          tags: item.tags || [],
          email: item.email,
          phone: item.phone_number,
          notes: item.shared_notes,
          deal_size: item.deal_size,
          source: item.source,
          score: item.score,
          last_contacted: item.last_contacted,
        };
      } else {
        const boardItemData: CardBoard = item;
        return {
          type: 'board',
          id: boardItemData.id,
          title: boardItemData.title || 'No Title',
          subtitle: boardItemData.subtitle,
          description: boardItemData.description,
          tags: boardItemData.tags || [],
          order: boardItemData.order,
          status: boardItemData.status,
          itemable_type: boardItemData.itemable_type,
          itemable_id: boardItemData.itemable_id,
          owner_object: item.owner_object || null,
        };
      }
    });

    let listOrder: number | undefined;
    if (mode === 'contacts') {
      const match = String(list.title).match(/^(\d+)-/);
      if (match) {
        listOrder = parseInt(match[1], 10);
      } else {
        listOrder = list.title === 'Backlog' ? -1 : 999;
      }
    } else {
      listOrder = list.order;
    }

    return {
      id: list.id,
      title: list.title,
      cards: adaptedCards.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
      order: listOrder,
    };
  });
};

// --- MAIN COMPONENT ---
type IListAndCardsProps = {
  boardMode: 'contacts' | 'boards';
  boardId?: string;
};

export const ListsAndCards = ({ boardMode, boardId }: IListAndCardsProps) => {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  const [lists, setLists] = useState<KanbanListType[]>([]);
  const [activeDraggedItem, setActiveDraggedItem] =
    useState<ActiveDraggedItem | null>(null);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [deletingModalLoading, setDeletingModalLoading] = useState(false);
  const [modalConfirmDeleteCardOpen, setModalConfirmDeleteCardOpen] =
    useState(false);
  const [cardToDelete, setCardToDelete] = useState<Card | null>(null);
  const [draggingNodeRect, setDraggingNodeRect] = useState<DOMRect | null>(
    null
  );
  const activatorEventRef = useRef<MouseEvent | TouchEvent | null>(null);
  const [initialOffset, setInitialOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  // State for Add/Edit Card Dialog
  const [isAddCardDialogOpen, setIsAddCardDialogOpen] = useState(false);
  const [editingCard, setEditingCard] = useState<Card | null>(null);
  const [cardTitle, setCardTitle] = useState('');
  const [cardDescription, setCardDescription] = useState('');
  const [currentListId, setCurrentListId] = useState<string | null>(null);
  const [selectedContact, setSelectedContact] = useState<any | null>(null);

  const [stageModal, setStageModal] = useState<{
    open: boolean;
    mode:
      | 'add-contact-stage'
      | 'rename-contact-stage'
      | 'rename-board-list'
      | 'add-board-list'
      | null;
    list?: KanbanListType | null;
    loading: boolean;
  }>({ open: false, mode: null, list: null, loading: false });

  const [deleteListModal, setDeleteListModal] = useState<{
    open: boolean;
    list: KanbanListType | null;
    loading: boolean;
  }>({ open: false, list: null, loading: false });

  // State for toast messages
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });

  const handleCloseToast = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setToast((prev) => ({ ...prev, open: false }));
  };

  const showToast = (
    message: string,
    severity: 'success' | 'error' | 'info' | 'warning'
  ) => {
    setToast({ open: true, message, severity });
  };


  const fetchData = async () => {
    setLoading(true);
    try {
      let rawLists: any[] = [];
      const rawCardsByList: { [listId: string]: any[] } = {};

      if (boardMode === 'contacts') {
        const response = await ContactsService.fetch_contacts();
        const contacts = response.data?.contacts || [];
        const stagesMap: { [key: string]: { id: string; title: string } } = {};

        contacts.forEach((contact: any) => {
          const stage = contact.stage || 'Backlog';
          if (!stagesMap[stage]) {
            stagesMap[stage] = { id: stage, title: stage };
          }
          if (!rawCardsByList[stage]) {
            rawCardsByList[stage] = [];
          }
          rawCardsByList[stage].push(contact);
        });
        rawLists = Object.values(stagesMap);
      } else if (boardMode === 'boards' && boardId) {
        const listsResponse = await getAllBoardLists(boardId);
        rawLists = (listsResponse.lists || []).sort(
          (a: any, b: any) => (a.order || 0) - (b.order || 0)
        );
        await Promise.all(
          rawLists.map(async (list) => {
            const itemsResp = await getAllBoardListItems(list.id, true); // Request full payload
            rawCardsByList[list.id] =
              itemsResp.list_items || itemsResp.items || itemsResp.cards || [];
          })
        );
      }

      const processedLists = adaptApiResponseToKanbanLists(
        rawLists,
        rawCardsByList,
        boardMode
      );
      setLists(processedLists.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    } catch (error) {
      console.error('Error fetching data:', error);
      setLists([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [boardMode, boardId]);

  // Listen for list and list item updates from Pusher events
  useEffect(() => {
    if (boardMode !== 'boards') return;

    // Handle list events
    const handleListUpdated = async (event: CustomEvent) => {
      const { listId, list, boardId: eventBoardId, eventType } = event.detail;

      // Check if this event is for our current board
      if (eventBoardId && eventBoardId !== boardId) {
        return; // Skip if it's for a different board
      }

      if (eventType === 'list_created') {
        // Add new list to our state
        const newList = {
          ...list,
          cards: [], // New lists start empty
        };

        setLists((prevLists) => {
          const adaptedList = adaptApiResponseToKanbanLists(
            [newList],
            { [listId]: [] },
            boardMode
          )[0];

          const newLists = [...prevLists, adaptedList];
          return newLists.sort((a, b) => (a.order || 0) - (b.order || 0));
        });
      } else if (eventType === 'list_updated') {
        // Update existing list
        setLists((prevLists) => {
          return prevLists.map((existingList) => {
            if (existingList.id === listId) {
              return {
                ...existingList,
                ...list,
                cards: existingList.cards, // Keep existing cards
              };
            }
            return existingList;
          });
        });
      }
    };

    const handleListDeleted = (event: CustomEvent) => {
      const { listId, boardId: eventBoardId } = event.detail;

      // Check if this event is for our current board
      if (eventBoardId && eventBoardId !== boardId) {
        return;
      }

      // Remove the list from our state
      setLists((prevLists) => prevLists.filter((list) => list.id !== listId));
    };

    const handleListItemUpdated = async (event: CustomEvent) => {
      const { listId, itemId, listItem, boardId: eventBoardId } = event.detail;

      // Check if this event is for our current board
      if (eventBoardId && eventBoardId !== boardId) {
        return; // Skip if it's for a different board
      }

      // Check if we have the list in our current state
      const listExists = lists.some((list) => list.id === listId);

      if (!listExists) {
        // List doesn't exist, we need to fetch it
        try {
          const listResponse = await getList(listId);
          if (listResponse.success && listResponse.list) {
            const newList = listResponse.list;

            // Fetch all items for this new list
            const itemsResp = await getAllBoardListItems(listId, true); // Request full payload
            const cards = (
              itemsResp.list_items ||
              itemsResp.items ||
              itemsResp.cards ||
              []
            )
              .map((c: any, idx: number) => ({
                ...c,
                id: String(c.id),
                order: typeof c.order === 'number' ? c.order : idx,
              }))
              .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));

            // Add the new list with its cards to our state
            setLists((prevLists) => {
              const adaptedList = adaptApiResponseToKanbanLists(
                [newList],
                { [listId]: cards },
                boardMode
              )[0];

              // Insert the new list in the correct position based on order
              const newLists = [...prevLists, adaptedList];
              return newLists.sort((a, b) => (a.order || 0) - (b.order || 0));
            });
          }
        } catch (error) {
          console.error('Error fetching missing list:', error);
        }
      } else {
        // List exists, update the specific list item in our state
        setLists((prevLists) => {
          return prevLists.map((list) => {
            if (list.id === listId) {
              // Check if the item exists or needs to be added
              const cardIndex = list.cards.findIndex(
                (card) => card.id === itemId
              );
              let updatedCards;

              if (cardIndex !== -1) {
                // Update existing card
                updatedCards = list.cards.map((card) =>
                  card.id === itemId ? { ...card, ...listItem } : card
                );
              } else {
                // Add new card
                const newCard = {
                  ...listItem,
                  id: String(listItem.id),
                  order:
                    typeof listItem.order === 'number'
                      ? listItem.order
                      : list.cards.length,
                };
                updatedCards = [...list.cards, newCard].sort(
                  (a, b) => (a.order || 0) - (b.order || 0)
                );
              }

              return { ...list, cards: updatedCards };
            }
            return list;
          });
        });
      }
    };

    const handleListItemDeleted = (event: CustomEvent) => {
      const { listId, itemId } = event.detail;

      // Remove the deleted item from our state
      setLists((prevLists) => {
        return prevLists.map((list) => {
          if (list.id === listId) {
            const updatedCards = list.cards.filter(
              (card) => card.id !== itemId
            );
            return { ...list, cards: updatedCards };
          }
          return list;
        });
      });
    };

    // Add all event listeners
    window.addEventListener('listUpdated', handleListUpdated as EventListener);
    window.addEventListener('listDeleted', handleListDeleted as EventListener);
    window.addEventListener(
      'listItemUpdated',
      handleListItemUpdated as EventListener
    );
    window.addEventListener(
      'listItemDeleted',
      handleListItemDeleted as EventListener
    );

    return () => {
      window.removeEventListener(
        'listUpdated',
        handleListUpdated as EventListener
      );
      window.removeEventListener(
        'listDeleted',
        handleListDeleted as EventListener
      );
      window.removeEventListener(
        'listItemUpdated',
        handleListItemUpdated as EventListener
      );
      window.removeEventListener(
        'listItemDeleted',
        handleListItemDeleted as EventListener
      );
    };
  }, [boardMode, boardId, lists]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    })
  );

  // Custom collision detection for nested sortables
  const customCollisionDetection = useCallback((args: any) => {
    // If we're dragging a card, use pointerWithin for better accuracy
    if (activeDraggedItem?.type === 'card') {
      const pointerCollisions = pointerWithin(args);
      const overList = pointerCollisions.find(
        (collision) => collision.data?.droppable?.data?.type === 'list'
      );

      if (overList) {
        return [overList];
      }

      // Fall back to closestCenter for card-to-card collisions
      return closestCenter(args);
    }

    // For lists, use closestCenter
    return closestCenter(args);
  }, [activeDraggedItem]);

  const handleOpenDeleteCardModal = (card: Card) => {
    setCardToDelete(card);
    setModalConfirmDeleteCardOpen(true);
  };

  const handleCloseDeleteCardModal = () => {
    setModalConfirmDeleteCardOpen(false);
    setDeletingModalLoading(false);
    setTimeout(() => setCardToDelete(null), 300);
  };

  const handleConfirmDeleteCard = async () => {
    if (!cardToDelete) return;

    // Optimistically remove the card from the UI
    const originalLists = JSON.parse(JSON.stringify(lists));
    let listIdContainingCard: string | null = null;
    setLists((prevLists) => {
      const newLists = prevLists.map((list) => {
        const updatedCards = list.cards.filter(
          (card) => card.id !== cardToDelete.id
        );
        if (updatedCards.length < list.cards.length) {
          listIdContainingCard = list.id;
        }
        return { ...list, cards: updatedCards };
      });
      return newLists;
    });

    setDeletingModalLoading(true);

    try {
      if (boardMode === 'contacts') {
        await ContactsService.destroy(cardToDelete.id);
      } else if (boardMode === 'boards') {
        if (listIdContainingCard) {
          await deleteListItem(listIdContainingCard, cardToDelete.id);
        } else {
          console.error('List ID for the card to delete was not found.');
          throw new Error('List ID not found');
        }
      }
    } catch (error) {
      console.error('Error deleting card:', error);
      setLists(originalLists); // Revert to original state on error
      showToast('Failed to delete the card. Please try again.', 'error');
    } finally {
      handleCloseDeleteCardModal();
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const type = active.data.current?.type as 'card' | 'list' | undefined;
    const itemData = active.data.current?.item;
    // Salva o evento de ativação e o rect do nó original
    activatorEventRef.current = event.activatorEvent as MouseEvent | TouchEvent;
    setDraggingNodeRect(active.rect.current.initial as DOMRect);

    // Calculate the offset of the container from viewport
    if (containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      setInitialOffset({
        x: -containerRect.left,
        y: -containerRect.top
      });
    }

    if (type && itemData) {
      setActiveDraggedItem({ id: active.id as string, type, data: itemData });
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveDraggedItem(null);
    setDraggingNodeRect(null);
    activatorEventRef.current = null;
    setInitialOffset({ x: 0, y: 0 });
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;
    const activeType = active.data.current?.type;

    if (activeType === 'list' && activeId !== overId) {
      // Optimistically update the UI
      const originalLists = JSON.parse(JSON.stringify(lists)); // Deep copy for rollback
      setLists((currentLists) => {
        const oldIndex = currentLists.findIndex((l) => l.id === activeId);
        const newIndex = currentLists.findIndex((l) => l.id === overId);
        const reorderedLists = arrayMove(currentLists, oldIndex, newIndex);

        // Update the order property for each list based on its new index
        const updatedOrderLists = reorderedLists.map((list, index) => ({
          ...list,
          order: index, // Set the order to the current index
        }));

        // Now, send updates to the backend
        if (boardMode === 'boards') {
          updatedOrderLists.forEach(async (list) => {
            try {
              console.log(`Updating list ${list.id} with order: ${list.order}`);
              await updateList(list.id, { order: list.order });
            } catch (error) {
              console.error(
                `Failed to update order for list ${list.id}:`,
                error
              );
              // In case of error, revert all lists to original state
              setLists(originalLists);
              showToast(
                'Failed to update list order. Please try again.',
                'error'
              );
              return; // Stop further updates if one fails
            }
          });
        }
        return updatedOrderLists;
      });
      return; // Exit after handling list reordering
    }

    if (activeType === 'card') {
      const originalLists = JSON.parse(JSON.stringify(lists));

      // Find source and destination info
      const sourceList = lists.find((l) => l.cards.some((c) => c.id === activeId));
      if (!sourceList) return;

      const sourceCardIndex = sourceList.cards.findIndex((c) => c.id === activeId);
      const movedCard = sourceList.cards[sourceCardIndex];
      if (!movedCard) return;

      // Determine destination list
      const overIsList = over.data.current?.type === 'list';
      const overIsCard = over.data.current?.type === 'card';
      const destinationListId = overIsList
        ? overId
        : lists.find((l) => l.cards.some((c) => c.id === overId))?.id;

      if (!destinationListId) return;

      const destinationList = lists.find((l) => l.id === destinationListId);
      if (!destinationList) return;

      // Calculate the correct destination index
      let destinationCardIndex: number;

      if (overIsCard) {
        const overCardIndex = destinationList.cards.findIndex((c) => c.id === overId);

        if (sourceList.id === destinationList.id) {
          // Same list: need to account for the removal of the source card
          if (sourceCardIndex < overCardIndex) {
            // Dragging down: place after the target
            destinationCardIndex = overCardIndex;
          } else {
            // Dragging up: place before the target
            destinationCardIndex = overCardIndex;
          }
        } else {
          // Different list: just use the target position
          destinationCardIndex = overCardIndex;
        }
      } else {
        // Dropping on a list (not a card): add to the end
        destinationCardIndex = destinationList.cards.length;
      }

      // Create new state with arrayMove for same list or manual splice for different lists
      const newListsState = lists.map((list) => {
        if (sourceList.id === destinationList.id && list.id === sourceList.id) {
          // Same list: use arrayMove for correct index handling
          const newCards = arrayMove(list.cards, sourceCardIndex, destinationCardIndex);
          return { ...list, cards: newCards };
        } else if (list.id === sourceList.id) {
          // Source list: remove the card
          return {
            ...list,
            cards: list.cards.filter(c => c.id !== activeId)
          };
        } else if (list.id === destinationListId) {
          // Destination list: add the card
          const newCards = [...list.cards];
          newCards.splice(destinationCardIndex, 0, movedCard);
          return { ...list, cards: newCards };
        }
        return list;
      });

      setLists(newListsState);

      // --- Backend Updates ---
      const hasMovedToList = sourceList.id !== destinationList.id;

      // Get the updated lists from newListsState for backend updates
      const updatedSourceList = newListsState.find(l => l.id === sourceList.id);
      const updatedDestList = newListsState.find(l => l.id === destinationList.id);

      try {
        if (boardMode === 'boards') {
          const updatePromises = [];

          if (hasMovedToList) {
            await moveListItemToAnotherList(
              sourceList.id,
              destinationList.id,
              movedCard.id
            );
          }

          // Update positions for cards in destination list using the new state
          if (updatedDestList) {
            updatedDestList.cards.forEach((card, index) => {
              updatePromises.push(
                updatePositionListItem(destinationList.id, card.id, index)
              );
            });
          }

          // Update positions for cards in source list if different
          if (hasMovedToList && updatedSourceList) {
            updatedSourceList.cards.forEach((card, index) => {
              updatePromises.push(
                updatePositionListItem(sourceList.id, card.id, index)
              );
            });
          }
          await Promise.all(updatePromises);
        } else if (boardMode === 'contacts') {
          const updatePromises: Promise<any>[] = [];

          // Update positions for cards in destination list using the new state
          if (updatedDestList) {
            updatedDestList.cards.forEach((card, index) => {
              const payload: { position: number; stage?: string } = {
                position: index + 1,
              };
              if (card.id === activeId && hasMovedToList) {
                payload.stage = destinationListId;
              }
              updatePromises.push(
                ContactsService.update({ id: card.id, contact: payload })
              );
            });
          }

          // Update positions for cards in source list if different
          if (hasMovedToList && updatedSourceList) {
            updatedSourceList.cards.forEach((card, index) => {
              updatePromises.push(
                ContactsService.update({
                  id: card.id,
                  contact: { position: index + 1 },
                })
              );
            });
          }

          await Promise.all(updatePromises);
        }
      } catch (error) {
        console.error('Failed to update card position/list:', error);
        setLists(originalLists); // Revert on failure
        showToast('Failed to save the changes. Please try again.', 'error');
      }
    }
  };

  const handleToggleCardStatus = async (cardId: string) => {
    // 1. Capture the original state for potential rollback
    const originalLists = JSON.parse(JSON.stringify(lists));
    let updatedListId: string | null = null;
    let newStatus: string | null = null;

    // 2. Optimistically update the UI
    setLists((prevLists) =>
      prevLists.map((list) => ({
        ...list,
        cards: list.cards.map((card) => {
          if (card.id === cardId && card.type === 'board') {
            updatedListId = list.id; // Capture the listId
            newStatus = card.status === 'active' ? 'inactive' : 'active';
            return {
              ...card,
              status: newStatus,
            };
          }
          return card;
        }),
      }))
    );

    // 3. Make the API call
    if (updatedListId && newStatus !== null) {
      try {
        await updateListItem(updatedListId, cardId, { status: newStatus });
        showToast('Card status updated successfully!', 'success');
      } catch (error) {
        console.error('Failed to update card status:', error);
        // 4. Rollback UI on API failure
        setLists(originalLists);
        showToast('Failed to update card status. Please try again.', 'error');
      }
    }
  };

  // --- Handlers for Add/Edit Card Dialog ---
  const handleAddCard = (listId: string) => {
    if (boardMode !== 'boards') return;
    setEditingCard(null); // This signifies a new card
    setCurrentListId(listId);
    setCardTitle('');
    setCardDescription('');
    setSelectedContact(null);
    setIsAddCardDialogOpen(true);
  };

  const handleEditCard = (card: Card) => {
    if (card.type !== 'board') return;
    const listContainingCard = lists.find((l) =>
      l.cards.some((c) => c.id === card.id)
    );
    if (!listContainingCard) return;

    setEditingCard(card);
    setCurrentListId(listContainingCard.id);
    setCardTitle(card.title || '');
    setCardDescription(card.description || '');
    // The selectedContact will be set by the useEffect in IAddNewCardDialog
    setIsAddCardDialogOpen(true);
  };

  const handleCloseAddCardDialog = () => {
    setIsAddCardDialogOpen(false);
    // Delay resetting state to avoid flicker while dialog is closing
    setTimeout(() => {
      setEditingCard(null);
      setCurrentListId(null);
      setCardTitle('');
      setCardDescription('');
      setSelectedContact(null);
    }, 300);
  };

  const handleCardSubmit = async (data: {
    title: string;
    subtitle?: string;
    description: string;
    tags?: string[];
    itemable_type?: string | null;
    itemable_id?: string | null;
    clear_itemable?: boolean;
  }) => {
    const originalLists = JSON.parse(JSON.stringify(lists)); // For rollback

    if (editingCard && currentListId) {
      // --- UPDATE CARD ---
      // Include the selectedContact as owner_object for optimistic update
      const updatedCard = { 
        ...editingCard, 
        ...data,
        // Handle clearing itemable (only for board cards)
        ...(editingCard.type === 'board' ? {
          itemable_type: data.clear_itemable ? null : (data.itemable_type || editingCard.itemable_type),
          itemable_id: data.clear_itemable ? null : (data.itemable_id || editingCard.itemable_id),
          owner_object: data.clear_itemable ? null : (data.itemable_type === 'Contact' && selectedContact ? selectedContact : null)
        } : {})
      } as Card;
      
      // Optimistic UI update
      setLists((prevLists) =>
        prevLists.map((list) =>
          list.id === currentListId
            ? {
                ...list,
                cards: list.cards.map((c) =>
                  c.id === editingCard.id ? updatedCard : c
                ),
              }
            : list
        )
      );
      try {
        const response = await updateListItem(currentListId, editingCard.id, data);
        // Update with actual response data including owner_object
        if (response && response.success && response.list_item) {
          setLists((prevLists) =>
            prevLists.map((list) =>
              list.id === currentListId
                ? {
                    ...list,
                    cards: list.cards.map((c) =>
                      c.id === editingCard.id 
                        ? {
                            ...updatedCard,
                            itemable_type: response.list_item.itemable_type || null,
                            itemable_id: response.list_item.itemable_id || null,
                            owner_object: response.list_item.owner_object || null,
                          }
                        : c
                    ),
                  }
                : list
            )
          );
        }
        showToast('Card updated successfully!', 'success');
      } catch (error) {
        console.error('Failed to update card:', error);
        setLists(originalLists); // Rollback on failure
        showToast('Failed to update card. Please try again.', 'error');
      }
    } else if (currentListId) {
      // --- CREATE CARD ---
      try {
        const listForNewCard = lists.find((l) => l.id === currentListId);
        const newCardData = {
          ...data,
          order: listForNewCard ? listForNewCard.cards.length : 0,
          status: 'inactive',
        };
        // The service should return the newly created card with its ID
        const response = await createListItem(currentListId, newCardData);
        console.log('Create card response:', response);

        // The response might be wrapped in a success structure
        const cardData = response?.list_item || response;

        // Check if response is valid
        if (!cardData || (!cardData.id && !response?.success)) {
          console.error('Invalid response structure:', response);
          throw new Error('Invalid response from server');
        }

        const newCard: BoardItemCardType = {
          type: 'board',
          id: cardData.id || `temp-${Date.now()}`,
          title: cardData.title || data.title,
          subtitle: cardData.subtitle || data.subtitle,
          description: cardData.description || data.description,
          tags: cardData.tags || data.tags || [],
          order: cardData.order !== undefined ? cardData.order : newCardData.order,
          status: cardData.status || 'inactive',
          itemable_type: cardData.itemable_type || data.itemable_type || null,
          itemable_id: cardData.itemable_id || data.itemable_id || null,
          owner_object: cardData.owner_object || (data.itemable_type === 'Contact' && selectedContact ? selectedContact : null),
        };

        console.log('Created new card:', newCard);
        console.log('Current list ID:', currentListId);
        console.log('Current lists before update:', lists);

        // Update UI with the card from the server
        setLists((prevLists) => {
          const updatedLists = prevLists.map((list) => {
            if (list.id === currentListId) {
              const updatedList = {
                ...list,
                cards: [...list.cards, newCard as Card]
              };
              console.log('Updated list:', updatedList);
              return updatedList;
            }
            return list;
          });
          console.log('Updated lists:', updatedLists);
          return updatedLists;
        });

        showToast('Card created successfully!', 'success');

        // Clear the form after successful creation
        setCardTitle('');
        setCardDescription('');
        setSelectedContact(null);
      } catch (error) {
        console.error('Failed to create card:', error);
        showToast('Failed to create card. Please try again.', 'error');
        // Re-throw to prevent dialog from closing
        throw error;
      }
    }
  };

  const handleToggleFavorite = async (cardId: string) => {
    let cardToUpdate: Card | null = null;
    let listId: string | null = null;
    let originalTags: string[] = [];

    const newLists = lists.map((list) => ({
      ...list,
      cards: list.cards.map((card) => {
        if (card.id === cardId) {
          cardToUpdate = card;
          listId = list.id;
          originalTags = [...card.tags];
          const isFavorite = card.tags.includes('isFavorite');
          const newTags = isFavorite
            ? card.tags.filter((t) => t !== 'isFavorite')
            : [...card.tags, 'isFavorite'];
          return { ...card, tags: newTags };
        }
        return card;
      }),
    }));

    if (!cardToUpdate) return;
    setLists(newLists);

    try {
      if (cardToUpdate.type === 'contact') {
        const newTags = (cardToUpdate as any).tags.includes('isFavorite')
          ? (cardToUpdate as any).tags.filter((t: any) => t !== 'isFavorite')
          : [...(cardToUpdate as any).tags, 'isFavorite'];

        await ContactsService.update({
          id: cardId,
          contact: { tags: newTags },
        });
      } else if (cardToUpdate.type === 'board') {
        // Find the list ID for the board item
        const containingList = lists.find((list) =>
          list.cards.some((c) => c.id === cardId)
        );
        if (containingList) {
          const newTags = (cardToUpdate as any).tags.includes('isFavorite')
            ? (cardToUpdate as any).tags.filter((t: any) => t !== 'isFavorite')
            : [...(cardToUpdate as any).tags, 'isFavorite'];

          await updateListItem(containingList.id, cardId, {
            tags: newTags,
          });
        } else {
          console.error('Containing list not found for board item:', cardId);
          throw new Error('Containing list not found');
        }
      }
    } catch (error) {
      console.error('Failed to update favorite status:', error);
      // Revert UI on error
      setLists((prev) =>
        prev.map((l) =>
          l.id === listId
            ? {
                ...l,
                cards: l.cards.map((c) =>
                  c.id === cardId ? { ...c, tags: originalTags } : c
                ),
              }
            : l
        )
      );
      showToast('Could not update favorite status. Please try again.', 'error');
    }
  };

  // --- STAGE/LIST MODAL HANDLERS ---
  const handleCloseStageModal = () => {
    setStageModal({ open: false, mode: null, list: null, loading: false });
  };

  const handleOpenAddListModal = () => {
    if (boardMode === 'contacts') {
      setStageModal({
        open: true,
        mode: 'add-contact-stage',
        list: null,
        loading: false,
      });
    } else if (boardMode === 'boards') {
      setStageModal({
        open: true,
        mode: 'add-board-list',
        list: null,
        loading: false,
      });
    }
  };

  const handleOpenRenameContactListModal = (listToRename: KanbanListType) => {
    setStageModal({
      open: true,
      mode: 'rename-contact-stage',
      list: listToRename,
      loading: false,
    });
  };

  const handleOpenRenameBoardListModal = (listToRename: KanbanListType) => {
    setStageModal({
      open: true,
      mode: 'rename-board-list',
      list: listToRename,
      loading: false,
    });
  };

  const handleSaveStageName = async (name: string) => {
    setStageModal((s) => ({ ...s, loading: true }));
    const { mode, list: listToEdit } = stageModal;

    if (mode === 'add-contact-stage') {
      handleSaveNewList(name);
    } else if (mode === 'rename-contact-stage' && listToEdit) {
      await handleSaveRenameList(listToEdit, name);
    } else if (mode === 'rename-board-list' && listToEdit) {
      const originalLists = lists;
      setLists((prev) =>
        prev.map((l) => (l.id === listToEdit.id ? { ...l, title: name } : l))
      );
      try {
        await updateList(listToEdit.id, { title: name });
      } catch (error) {
        console.error('Failed to rename board list', error);
        setLists(originalLists); // Revert on failure
        showToast('Failed to rename list.', 'error');
      }
    } else if (mode === 'add-board-list') {
      // Logic for adding a new board list
      if (!boardId) {
        showToast('Board ID is not available to create a new list.', 'error');
        handleCloseStageModal();
        return;
      }

      const originalLists = lists; // Capture state for rollback

      const highestOrder = lists.reduce(
        (max, list) =>
          list.order !== undefined && list.order > max ? list.order : max,
        -1 // Start from -1 for initial empty state
      );
      const newOrder = highestOrder + 1;

      // Optimistic update
      const tempId = `temp-list-${Date.now()}`;
      const newList: KanbanListType = {
        id: tempId,
        title: name,
        cards: [],
        order: newOrder,
      };

      setLists((prevLists) =>
        [...prevLists, newList].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      );

      try {
        const response = await createList({
          listable_type: 'Board',
          listable_id: boardId,
          title: name,
          order: newOrder,
        });

        // Replace the temporary list with the actual one from API response
        setLists((prevLists) =>
          prevLists
            .map((list) =>
              list.id === tempId ? { ...list, id: response.id } : list
            )
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        );
        showToast('New list created successfully!', 'success');
      } catch (error) {
        console.error('Failed to create new board list:', error);
        setLists(originalLists); // Rollback on failure
        showToast('Failed to create new list. Please try again.', 'error');
      }
    }
    handleCloseStageModal();
  };

  const handleSaveNewList = (descriptiveName: string) => {
    const trimmedName = descriptiveName.trim();
    if (
      lists.some(
        (list) =>
          list.title.replace(/^(\d+)-/, '').toLowerCase() ===
          trimmedName.toLowerCase()
      )
    ) {
      showToast(
        'A stage with this descriptive name already exists.',
        'warning'
      );
      return;
    }
    const highestOrder = lists.reduce(
      (max, list) =>
        list.order !== undefined && list.order > max ? list.order : max,
      0
    );
    const newOrder = highestOrder + 1;
    const formattedStageName = `${newOrder}-${trimmedName}`;
    const newList: KanbanListType = {
      id: formattedStageName,
      title: formattedStageName,
      cards: [],
      order: newOrder,
    };
    setLists((prevLists) =>
      [...prevLists, newList].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    );
  };

  const handleSaveRenameList = async (
    listToRename: KanbanListType,
    newDescriptiveName: string
  ) => {
    const oldStageId = listToRename.id;
    const order = listToRename.order;
    const newStageId = `${order}-${newDescriptiveName}`;

    if (oldStageId === newStageId) return;
    if (
      lists.some(
        (list) =>
          list.id.toLowerCase() === newStageId.toLowerCase() &&
          list.id !== oldStageId
      )
    ) {
      showToast('A stage with this name already exists.', 'warning');
      return;
    }

    const originalLists = lists;
    setLists((prevLists) =>
      prevLists.map((list) =>
        list.id === oldStageId
          ? { ...list, id: newStageId, title: newStageId }
          : list
      )
    );

    try {
      await Promise.all(
        listToRename.cards.map((card) =>
          ContactsService.update({
            id: card.id,
            contact: { stage: newStageId },
          })
        )
      );
    } catch (error) {
      console.error('Failed to update contacts:', error);
      setLists(originalLists);
      showToast('An error occurred while renaming. Please try again.', 'error');
    }
  };

  // --- DELETE LIST HANDLERS ---
  const handleOpenDeleteListModal = (list: KanbanListType) => {
    setDeleteListModal({ open: true, list, loading: false });
  };

  const handleDeleteBoardList = (list: KanbanListType) => {
    setDeleteListModal({ open: true, list, loading: false });
  };

  const handleCloseDeleteListModal = () => {
    setDeleteListModal({ open: false, list: null, loading: false });
  };

  const handleConfirmDeleteList = async () => {
    const listToDelete = deleteListModal.list;
    if (!listToDelete) return;

    // Check for cards in board mode before proceeding with deletion
    if (boardMode === 'boards' && listToDelete.cards.length > 0) {
      showToast(
        'He cannot delete the list if the list contains items under',
        'warning'
      );
      handleCloseDeleteListModal();
      return;
    }

    setDeleteListModal((s) => ({ ...s, loading: true }));
    const originalLists = JSON.parse(JSON.stringify(lists)); // Deep copy for rollback

    // Optimistically remove the list from UI
    setLists((prevLists) => prevLists.filter((l) => l.id !== listToDelete.id));

    try {
      if (boardMode === 'contacts') {
        const cardsToMove = listToDelete.cards;
        if (cardsToMove.length > 0) {
          // Move cards to Backlog for contacts mode
          await Promise.all(
            cardsToMove.map((card) =>
              ContactsService.update({
                id: card.id,
                contact: { stage: 'Backlog' },
              })
            )
          );
        }
        // No specific API call for deleting a contact stage (it's implicit by contacts not referencing it)
      } else if (boardMode === 'boards') {
        // Delete the board list using the provided function
        await deleteList(listToDelete.id);
      }
    } catch (error) {
      console.error('Failed to delete list:', error);
      setLists(originalLists); // Revert UI on error
      // showToast('Failed to delete the list. Please try again.', 'error');
      showToast(
        'Sorry, you need to move it to another list, or remove all the cards from the list to be able to delete it',
        'error'
      );
    } finally {
      handleCloseDeleteListModal();
    }
  };

  if (loading) {
    const isSmallScreen =
      typeof window !== 'undefined' ? window.innerWidth < 1500 : false;
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          minHeight: 340,
          gap: 4,
          flexWrap: 'wrap',
          mb: 2,
          p: 0,
        }}
      >
        {[...Array(isSmallScreen ? 1 : 3)].map((_, idx) => (
          <ListColumnSkeleton key={idx} />
        ))}
      </Box>
    );
  }

  return (
    <>
      <Box
        ref={containerRef}
        sx={{
          display: 'flex',
          gap: 2,
          p: 2,
          pb: 12, // Increased bottom padding to prevent cropping
          overflowX: 'auto',
          // minHeight: 'calc(100vh - 40px)', // Adjusted height calculation
          // maxHeight: 'calc(100vh - 40px)', // Added max height
          height: '100%',
          boxSizing: 'border-box',
          alignItems: 'flex-start',
          background: darkMode
            ? `linear-gradient(135deg, ${alpha(
                theme.palette.background.default,
                0.95
              )} 0%, ${alpha(theme.palette.background.default, 0.85)} 100%)`
            : `linear-gradient(135deg, ${alpha('#ffffff', 0.95)} 0%, ${alpha(
                '#ffffff',
                0.85
              )} 100%)`,
          backdropFilter: 'blur(10px)',
          '&::-webkit-scrollbar': { display: 'none' },
          '-ms-overflow-style': 'none',
          'scrollbar-width': 'none',
        }}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={customCollisionDetection}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={lists.map((l) => l.id)}
            strategy={horizontalListSortingStrategy}
          >
            {lists.map((list) => (
              <>
                <KanbanListColumn
                  key={list.id}
                  list={list}
                  onAddCard={handleAddCard}
                  onToggleCardStatus={handleToggleCardStatus}
                  onDeleteCard={handleOpenDeleteCardModal}
                  onRenameContactList={handleOpenRenameContactListModal}
                  onDeleteContactList={handleOpenDeleteListModal}
                  onRenameBoardList={handleOpenRenameBoardListModal}
                  onDeleteBoardList={handleDeleteBoardList}
                  onToggleFavorite={handleToggleFavorite}
                  onEditCard={handleEditCard}
                  boardMode={boardMode}
                />
              </>
            ))}
          </SortableContext>

          <AddNewColumnButton
            typeOfRender={boardMode}
            onClick={handleOpenAddListModal}
          />

          <DragOverlay
            dropAnimation={null}
            zIndex={9999}
            modifiers={[
              ({ transform }) => {
                return {
                  ...transform,
                  x: transform.x + initialOffset.x,
                  y: transform.y + initialOffset.y,
                };
              }
            ]}
          >
            {activeDraggedItem?.type === 'card' && (
              <CardItem
                card={activeDraggedItem.data as Card}
                isOverlay
                overlayWidth={draggingNodeRect?.width}
              />
            )}
            {activeDraggedItem?.type === 'list' && (
              <ListColumnItem
                title={(activeDraggedItem.data as KanbanListType).title}
                cards={(activeDraggedItem.data as KanbanListType).cards}
                isOverlay
              />
            )}
          </DragOverlay>
        </DndContext>
      </Box>

      <CustomScrollbar containerRef={containerRef} />

      <StageNameModal
        open={stageModal.open}
        onClose={handleCloseStageModal}
        onSave={handleSaveStageName}
        title={
          stageModal.mode === 'add-contact-stage'
            ? 'Add New Stage'
            : stageModal.mode === 'add-board-list'
            ? 'Add New Column'
            : 'Rename'
        }
        initialValue={stageModal.list?.title || ''}
        label={
          stageModal.mode === 'add-contact-stage' ||
          stageModal.mode === 'add-board-list'
            ? 'Column Name'
            : 'Stage Name'
        }
        loading={stageModal.loading}
      />

      <ConfirmDeleteCardDialog
        loading={deletingModalLoading}
        boardMode={boardMode}
        cardTitle={cardToDelete?.title}
        open={modalConfirmDeleteCardOpen}
        onConfirm={handleConfirmDeleteCard}
        onClose={handleCloseDeleteCardModal}
      />

      <ConfirmDeleteListDialog
        open={deleteListModal.open}
        onClose={handleCloseDeleteListModal}
        onConfirm={handleConfirmDeleteList}
        listTitle={deleteListModal.list?.title || ''}
        cardCount={deleteListModal.list?.cards.length || 0}
        loading={deleteListModal.loading}
        boardMode={boardMode}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
          sx={{
            width: '100%',
            maxWidth: 400,
            borderRadius: 2,
            background: darkMode
              ? alpha(theme.palette.background.paper, 0.95)
              : theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            backdropFilter: 'blur(10px)',
            boxShadow: theme.shadows[8],
          }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
      <IAddNewCardDialog
        onDialogOpen={isAddCardDialogOpen}
        onCardClose={handleCloseAddCardDialog}
        cardTitle={cardTitle}
        setCardTitle={setCardTitle}
        cardDescription={cardDescription}
        setCardDescription={setCardDescription}
        selectedContact={selectedContact}
        setSelectedContact={setSelectedContact}
        onCardSubmit={handleCardSubmit}
        editingCard={editingCard}
      />
    </>
  );
};

const ListColumnSkeleton: React.FC = () => {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  return (
    <Box
      sx={{
        minWidth: 300,
        maxWidth: 330,
        width: '100%',
        bgcolor: darkMode ? 'background.paper' : 'background.default',
        borderRadius: 2,
        boxShadow: 1,
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        border: '1px solid',
        borderColor: 'divider',
        height: 370,
        overflow: 'hidden',
      }}
    >
      {/* Header da coluna */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Skeleton variant="circular" width={28} height={28} />
        <Skeleton variant="text" width="60%" height={24} />
        <Box sx={{ flex: 1 }} />
        <Skeleton variant="circular" width={24} height={24} />
        <Skeleton variant="circular" width={24} height={24} />
      </Box>
      {/* Cards skeleton */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          px: 2,
          py: 2,
        }}
      >
        <Skeleton
          variant="rectangular"
          width="100%"
          height={56}
          sx={{ borderRadius: 2 }}
        />
        <Skeleton
          variant="rectangular"
          width="100%"
          height={56}
          sx={{ borderRadius: 2 }}
        />
      </Box>
      {/* Botão adicionar card */}
      <Box sx={{ px: 2, pb: 2 }}>
        <Skeleton
          variant="rounded"
          width="80%"
          height={36}
          sx={{ borderRadius: 2 }}
        />
      </Box>
    </Box>
  );
};

export default ListsAndCards;
